import { AIVideo } from 'almanac-ui/icons/16/AIVideo'
import { VisualStyle } from 'almanac-ui/icons/16/VisualStyle'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { Credits } from 'blaze-ui/icons/20/Credits'
import { VideoOn } from 'blaze-ui/icons/20/VideoOn'
import { Image03 } from 'blaze-ui/icons/24/Image03'
import { Text } from 'blaze-ui/Text/Text'
import { SourceMediaInfo } from 'hooks/queries/files/types'
import { useCreditUsage } from 'hooks/useCreditUsage'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { observer } from 'mobx-react-lite'
import { CONTENT_PREFERENCES_PATH } from 'paths'
import { generatePath, Link } from 'react-router-dom'
import { useRootStore } from 'stores/RootStore'

import { ToolbarMenu } from '../Toolbar/ToolbarMenu'
import Styles from './AboutPost.module.scss'
import { ArrowRight } from 'blaze-ui/icons'
import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import { useShowNextGenContentPreferences } from 'hooks/useShowNextGenContentPreferences'

interface AboutPostProps {
  sourceMediaInfo?: SourceMediaInfo | null
  onClose?: () => void
}

interface MediaTag {
  label: string
  icon: JSX.Element
}

const getAIImageTag = (count?: number): MediaTag => {
  const label = count && count > 1 ? `${count} AI Generated Images` : 'AI Generated Image'
  return { label, icon: <VisualStyle size={16} /> }
}

const AI_GENERATED_VIDEO_TAG: MediaTag = {
  label: 'AI Generated Video',
  icon: <AIVideo size={16} />,
}

const STOCK_PHOTO_TAG: MediaTag = {
  label: 'Stock photo',
  icon: <Image03 size={16} />,
}

const STOCK_VIDEO_TAG: MediaTag = {
  label: 'Stock video',
  icon: <VideoOn size={16} />,
}

const BRAND_KIT_PHOTO_TAG: MediaTag = {
  label: 'Brand kit photo',
  icon: <Image03 size={16} />,
}

const BRAND_KIT_VIDEO_TAG: MediaTag = {
  label: 'Brand kit video',
  icon: <VideoOn size={16} />,
}

export const AboutPost = observer(({ sourceMediaInfo, onClose }: AboutPostProps) => {
  const { showNextGenContentPreferences } = useShowNextGenContentPreferences()
  const workspaceId = useCurrentWorkspaceId()
  const { usageCosts } = useCreditUsage()
  const {
    tierUsageStore: { currentSubscription },
  } = useRootStore()
  const isOnPioneer = currentSubscription?.plan === PaymentPlanKey.VirtualMarketer

  if (!sourceMediaInfo) return null

  const { provider, original_provider, mime_type, ai_image_count, is_generation_v2 } = sourceMediaInfo
  const isVideo = mime_type?.startsWith('video/')
  const isAIGeneratedImage = provider === 'ai_generated'
  const isAIGeneratedVideo = provider === 'veo3'
  const isCombinedAI = isAIGeneratedVideo && original_provider === 'ai_generated'

  const aiVideoCreditCost = usageCosts?.video_credits_cost ?? 0
  const aiImageCreditCost = usageCosts?.visual_styles_credits_cost ?? 0
  const contentCreditCost = usageCosts?.content_credits_cost ?? 0
  const v2ImageCreditCost = usageCosts?.still_image_credits_cost ?? 0
  const v2CarouselCreditCost = usageCosts?.carousel_credits_cost ?? 0
  const v2VideoCreditCost = usageCosts?.script_video_credits_cost ?? 0

  const isGeneratedByAI = isCombinedAI || isAIGeneratedImage || isAIGeneratedVideo

  const tags: MediaTag[] = []
  let totalCredits = 0

  if (isCombinedAI) {
    tags.push(getAIImageTag(ai_image_count))
    tags.push(AI_GENERATED_VIDEO_TAG)
    if (is_generation_v2) {
      totalCredits = v2VideoCreditCost
    } else {
      totalCredits = aiImageCreditCost + aiVideoCreditCost
    }
  } else if (isAIGeneratedImage) {
    tags.push(getAIImageTag(ai_image_count))
    if (is_generation_v2) {
      const isCarousel = (ai_image_count ?? 0) > 1
      totalCredits = isCarousel ? v2CarouselCreditCost : v2ImageCreditCost
    } else {
      totalCredits = aiImageCreditCost
    }
  } else if (isAIGeneratedVideo) {
    tags.push(AI_GENERATED_VIDEO_TAG)
    totalCredits = is_generation_v2 ? v2VideoCreditCost : aiVideoCreditCost
  } else {
    const isBrandKit = !provider
    if (isBrandKit) {
      tags.push(isVideo ? BRAND_KIT_VIDEO_TAG : BRAND_KIT_PHOTO_TAG)
    } else {
      tags.push(isVideo ? STOCK_VIDEO_TAG : STOCK_PHOTO_TAG)
    }
    totalCredits = isOnPioneer ? 0 : contentCreditCost
  }

  const creditsLabel = totalCredits === 1 ? 'credit' : 'credits'

  return (
    <ToolbarMenu title="About this post">
      <div className={Styles.container}>
        {tags.map((tag) => (
          <div key={tag.label} className={Styles.tag}>
            {tag.icon}
            <Text variant="secondary">{tag.label}</Text>
          </div>
        ))}
        {totalCredits > 0 && (
          <div className={Styles.tag}>
            <Credits size={16} />
            <Text variant="secondary">
              {totalCredits} {creditsLabel} used
            </Text>
          </div>
        )}
      </div>
      {isGeneratedByAI && showNextGenContentPreferences && (
        <div className={Styles.changeHowAIUsed}>
          <ButtonLink
            variant="secondary"
            size="xs"
            elementType={Link}
            endIcon={ArrowRight}
            to={`${generatePath(CONTENT_PREFERENCES_PATH, { workspaceId })}#content-modification`}
            onPress={onClose}
          >
            Change how AI is used
          </ButtonLink>
        </div>
      )}
    </ToolbarMenu>
  )
})
